import { Grid2, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import { CompanyDetailsResponse } from '@/models/payment';
import { SubTitle, Title } from '@/typography/Typography';
import {
    SecondaryButtonRegular,
    PrimaryButton
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { GridContainer } from '@/pages/company/componants/Base';
import { ButtonContainer } from '@/pages/company/PaymentSetting';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';
import { AutoTopUpSettings } from './AutoTopUpSettings';

export const LowBalanceSettings = ({
    currentCompany
}: {
    currentCompany?: CompanyDetailsResponse;
}) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { updateCompanyDetails } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    const [lowBalanceEmail, setLowBalanceEmail] = useState(
        currentCompany?.lowBalanceEmail || ''
    );
    const [lowBalanceThreshold, setLowBalanceThreshold] = useState(
        currentCompany?.lowBalanceNotificationThreshold || 0
    );
    const [lowBalanceEmailError, setLowBalanceEmailError] = useState(false);
    const [lowBalanceThresholdError, setLowBalanceThresholdError] =
        useState(false);

    // Auto Top-up state
    const isConnected = currentCompany?.autoTopupStatus === 'Connected';
    const [isAutoTopupEnabled, setIsAutoTopupEnabled] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(
        currentCompany?.autoTopupAmount || 0
    );

    const initialValues = () => {
        setLowBalanceEmail(currentCompany?.lowBalanceEmail || '');
        setLowBalanceThreshold(
            currentCompany?.lowBalanceNotificationThreshold || 0
        );
        setLowBalanceEmailError(false);
        setLowBalanceThresholdError(false);

        // Reset Auto Top-up state
        setIsAutoTopupEnabled(
            currentCompany?.autoTopupStatus === 'Connected' &&
                (currentCompany?.autoTopupAmount ?? 0) > 0
        );
        setTopUpAmount(currentCompany?.autoTopupAmount || 0);
    };

    useEffect(() => {
        initialValues();
    }, [
        currentCompany?.lowBalanceEmail,
        currentCompany?.lowBalanceNotificationThreshold,
        currentCompany?.autoTopupStatus,
        currentCompany?.autoTopupAmount
    ]);

    useEffect(() => {
        if (updateCompanyDetails.isSuccess) {
            setAlert(t('settings_saved'), 'success');
        }
    }, [updateCompanyDetails.isSuccess]);

    useEffect(() => {
        if (updateCompanyDetails.isError) {
            backendErrorHandler()(updateCompanyDetails.error);
        }
    }, [updateCompanyDetails.isError]);

    const isValidLowBalanceData = () => {
        let isValid = true;

        if (!lowBalanceThreshold) {
            setLowBalanceThresholdError(true);
            isValid = false;
        } else {
            setLowBalanceThresholdError(false);
        }

        if (!lowBalanceEmail) {
            setLowBalanceEmailError(true);
            isValid = false;
        } else {
            setLowBalanceEmailError(false);
        }

        return isValid;
    };

    const handleSaveChanges = () => {
        if (isValidLowBalanceData()) {
            let autoTopupAmountValue =
                isConnected && isAutoTopupEnabled && topUpAmount > 0
                    ? topUpAmount
                    : 0;

            if (isAutoTopupEnabled) {
                autoTopupAmountValue = 20000;
            }

            updateCompanyDetails.mutate({
                lowBalanceEmail,
                lowBalanceNotificationThreshold: Number(lowBalanceThreshold),
                autoTopupAmount: autoTopupAmountValue
            });
        }
    };

    const handleCancel = () => {
        initialValues();
    };

    return (
        <Grid2
            container
            mt={3}
            pt={5}
            justifyContent={'space-between'}
            borderTop={'1px solid #D0D5DD'}
        >
            <Grid2 size={{ xs: 3 }} display={'block'}>
                <Title>{t('low_balance')}</Title>
                <SubTitle marginBottom={2}>
                    {t('low_balance_description_1')}
                </SubTitle>
            </Grid2>
            <Grid2 size={{ xs: 8.8 }}>
                <Card>
                    <GridContainer container margin={'0 !important'}>
                        <InputText
                            type="number"
                            value={lowBalanceThreshold}
                            onChange={(e) =>
                                setLowBalanceThreshold(e.target.value as any)
                            }
                            label={t('set_low_balance_amount')}
                            placeholder="Default is set to 10,000 DKK"
                            error={lowBalanceThresholdError}
                        />
                        <SubTitle>
                            If the company balance drops below this number,
                            you'll receive an email reminder to deposit funds.
                        </SubTitle>
                    </GridContainer>
                    <GridContainer container>
                        <InputText
                            value={lowBalanceEmail}
                            onChange={(e) => setLowBalanceEmail(e.target.value)}
                            label={t('low_balance_notification')}
                            placeholder="Enter one or more email addresses (separated by commas)"
                            error={lowBalanceEmailError}
                        />
                        <SubTitle>Separate each email with a comma,</SubTitle>
                    </GridContainer>

                    <GridContainer
                        // mt={3}
                        container
                        borderTop={'1px solid #D0D5DD'}
                    >
                        <AutoTopUpSettings
                            currentCompany={currentCompany}
                            isAutoTopupEnabled={isAutoTopupEnabled}
                            setIsAutoTopupEnabled={setIsAutoTopupEnabled}
                            topUpAmount={topUpAmount}
                            setTopUpAmount={setTopUpAmount}
                        />
                    </GridContainer>

                    <ButtonContainer mt={3}>
                        <SecondaryButtonRegular onClick={handleCancel}>
                            {t('cancel')}
                        </SecondaryButtonRegular>
                        <PrimaryButton
                            sx={{ marginLeft: '6px !important' }}
                            size="medium"
                            loading={updateCompanyDetails.isPending}
                            onClick={handleSaveChanges}
                            disabled={!lowBalanceEmail || !lowBalanceThreshold}
                        >
                            {t('save_changes')}
                        </PrimaryButton>
                    </ButtonContainer>
                </Card>
            </Grid2>
        </Grid2>
    );
};
